import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const steps = [
  {
    num: '01',
    title: 'Consultation & Site Evaluation',
    desc: 'We visit your location, assess visibility challenges, and understand your brand and budget. Every recommendation is built around your specific business goals.',
    icon: 'ri-discuss-line',
  },
  {
    num: '02',
    title: 'Custom Design & Engineering',
    desc: 'Our designers create high-impact concepts tailored to your brand. We handle structural engineering, material selection, and compliance planning simultaneously.',
    icon: 'ri-pencil-ruler-2-line',
  },
  {
    num: '03',
    title: 'Permitting & Approvals',
    desc: 'We manage all city permit applications and HOA approvals. Our knowledge of Austin\'s sign codes means faster approvals and zero compliance surprises.',
    icon: 'ri-file-list-3-line',
  },
  {
    num: '04',
    title: 'Fabrication & Production',
    desc: 'Built in-house using premium-grade materials and precision manufacturing. Every component is quality-checked before it leaves our facility.',
    icon: 'ri-tools-line',
  },
  {
    num: '05',
    title: 'Professional Installation',
    desc: 'Our certified installation crew handles everything from structural mounting to electrical connection — safely, on schedule, built to last for years.',
    icon: 'ri-hammer-line',
  },
];

export default function ProcessSection() {
  const { ref, visible } = useScrollAnimation();

  return (
    <section id="process" className="relative py-28 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/5cb01e9c6854d5d5c518d90344722033.jpg"
          alt="Capital Area Signs fabrication process"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-[#080808]/94" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, #080808 0%, #080808/60 50%, #080808 100%)' }} />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-8">
        {/* Header */}
        <div ref={ref} className={`text-center mb-20 reveal ${visible ? 'visible' : ''}`}>
          <p className="eyebrow mb-5">Our Process</p>
          <h2 className="text-5xl md:text-6xl font-bold text-white leading-tight mb-6"
            style={{ fontFamily: 'Playfair Display, serif' }}>
            From Concept to Installation —<br />
            <em className="not-italic text-gradient-gold">A Seamless Process</em>
          </h2>
          <span className="gold-bar mx-auto block mb-6" />
          <p className="text-white/45 text-base max-w-2xl mx-auto leading-relaxed">
            We make every project simple, transparent, and results-driven. From first conversation to final install, our team manages every detail.
          </p>
        </div>

        {/* Steps */}
        <div className="relative">
          {/* Horizontal connector */}
          <div className="hidden lg:block absolute top-12 left-[10%] right-[10%] h-px"
            style={{ background: 'linear-gradient(to right, transparent, #C4962A/30, #C4962A/50, #C4962A/30, transparent)' }} />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-4">
            {steps.map((step, i) => (
              <div key={i} className={`flex flex-col items-center text-center group reveal delay-${i * 100} ${visible ? 'visible' : ''}`}>
                {/* Number circle */}
                <div className="relative w-24 h-24 flex items-center justify-center mb-6 flex-shrink-0">
                  <div className="absolute inset-0 rounded-full border border-[#C4962A]/25 group-hover:border-[#C4962A]/60 transition-colors duration-500" />
                  <div className="absolute inset-2 rounded-full border border-[#C4962A]/10 group-hover:border-[#C4962A]/30 transition-colors duration-500" />
                  <span className="text-3xl font-bold text-[#C4962A]/60 group-hover:text-[#C4962A] transition-colors duration-300"
                    style={{ fontFamily: 'Playfair Display, serif' }}>
                    {step.num}
                  </span>
                </div>

                <span className="w-8 h-8 flex items-center justify-center mb-4">
                  <i className={`${step.icon} text-[#C4962A] text-lg`} />
                </span>

                <h3 className="font-bold text-white text-sm mb-3 leading-snug"
                  style={{ fontFamily: 'Playfair Display, serif' }}>
                  {step.title}
                </h3>
                <p className="text-white/40 text-xs leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <a href="#quote" className="btn-gold">
            Start Your Project
            <i className="ri-arrow-right-line" />
          </a>
        </div>
      </div>
    </section>
  );
}
